/**
 * Phase 7C-4 -- owns every transition of the Website's session state.
 *
 * Plain functions over the existing Redux store rather than a new state
 * container: the app already keeps the current employee in
 * `store/user/slice.ts`, so the actor stays there and this module only
 * decides WHEN it changes and on WHOSE authority.
 *
 * THE ACTOR IS NEVER PERSISTED. It is rebuilt from `SESSION_ME` on every
 * page load, so a stale privilege, name or employee id in any browser
 * storage can never be mistaken for proof of authentication. The only
 * thing that survives a refresh is the token itself, in `sessionStorage`,
 * and it is always checked with the Backend before protected UI renders.
 *
 * FAIL CLOSED, BUT DO NOT DESTROY
 * ----------------------------------------------------------------------
 * A 401 on SESSION_ME means the token is genuinely dead: it is removed so
 * the next load does not repeat a request that can only fail. Anything
 * else (offline, timeout, 5xx) means "could not confirm": protected UI
 * stays hidden, the token is KEPT, and the user is offered a retry rather
 * than being made to re-type a password because the Wi-Fi dropped.
 *
 * NOTHING HERE LOGS A TOKEN.
 */
import type { AppDispatch, RootState } from '../store';
import {
    AuthActor,
    sessionCleared,
    sessionEstablished,
    setAuthStatus,
} from '../store/user/slice';
import {
    buildDeviceLabel,
    clearLegacyAuthState,
    clearSessionToken,
    getOrCreateDeviceId,
    readSessionToken,
    writeSessionToken,
} from './session-store';
import {
    SessionEmployee,
    SessionMeResult,
    session_create,
    session_me,
    session_revoke,
} from './session-api';
import { toAuthApiError } from './auth-errors';

/** The two store capabilities this module needs; matches the app store. */
export interface AuthStore {
    dispatch: AppDispatch;
    getState: () => RootState;
}

export interface SignInInput {
    code: string;
    password: string;
    /** True only after the user confirmed a 409 replacement. */
    replaceExisting?: boolean;
}

export interface SignOutResult {
    /** False when the Backend did not confirm the revoke (offline, dead token). */
    revokedRemotely: boolean;
}

const actorFromEmployee = (employee: SessionEmployee | undefined | null): AuthActor => {
    const source = employee ?? ({} as SessionEmployee);
    return {
        id: Number(source.id) || 0,
        code: source.code ?? '',
        name: source.name ?? '',
        privilege: Number(source.privilege) || 0,
        privilege_name: source.privilege_name ?? '',
        default_page: source.default_page ?? '',
    };
};

/** The authoritative actor, straight from `SESSION_ME`. */
export const actorFromSessionMe = (me: SessionMeResult): AuthActor => actorFromEmployee(me?.employee);

/**
 * Startup: decide between the login page and the app, using the Backend.
 *
 * Also the one place the legacy `localStorage` auth keys are removed --
 * before anything could read them, on every load.
 */
export const bootstrapSession = async (store: AuthStore): Promise<void> => {
    clearLegacyAuthState();

    const token = readSessionToken();
    if (!token) {
        // Never signed in on this tab -- nothing happened TO the user, so
        // no notice.
        store.dispatch(sessionCleared('none'));
        return;
    }
    await refreshSession(store);
};

/**
 * Re-resolves the actor from the Backend using the stored token.
 * Also the retry path from `unreachable`.
 */
export const refreshSession = async (store: AuthStore): Promise<void> => {
    store.dispatch(setAuthStatus('checking'));
    try {
        const me = await session_me();
        store.dispatch(sessionEstablished(actorFromSessionMe(me)));
    } catch (raw) {
        const error = toAuthApiError(raw);
        if (error.isUnauthenticated) {
            // There WAS a token and the Backend refused it: the session was
            // revoked (another device signed in, or someone signed out
            // elsewhere) or it expired. The user did not ask for this, so
            // the login page has to say why they are looking at it.
            clearSessionToken();
            store.dispatch(sessionCleared('sessionEnded'));
            return;
        }
        // Offline, timeout, 5xx, malformed body: the token may still be
        // perfectly good. Keep it, hide the app, offer a retry.
        store.dispatch(setAuthStatus('unreachable'));
    }
};

/**
 * Signs in. Resolves with the actor; rejects with an `AuthApiError` so
 * the login screen can branch on 401 / 403 / 409 / 429 itself.
 *
 * The token is written only after `session_create` has parsed a valid
 * response. The actor is then taken from `SESSION_ME`, which is the only
 * response that carries `privilege_name` and `default_page`; if that
 * follow-up cannot reach the Backend the employee block from the
 * successful SESSION_CREATE is used instead -- also a Backend response,
 * never anything the client typed.
 */
export const signIn = async (store: AuthStore, input: SignInInput): Promise<AuthActor> => {
    const deviceId = getOrCreateDeviceId();
    const created = await session_create({
        code: input.code,
        password: input.password,
        device_id: deviceId,
        device_label: buildDeviceLabel(deviceId),
        replace_existing: input.replaceExisting === true,
    });

    writeSessionToken(created.token);

    try {
        const me = await session_me();
        const actor = actorFromSessionMe(me);
        store.dispatch(sessionEstablished(actor));
        return actor;
    } catch (raw) {
        const error = toAuthApiError(raw);
        if (error.isUnauthenticated) {
            // The token we were just given is already refused (for example
            // replaced by another sign-in in the same instant). Do not keep
            // a credential the Backend has disowned. No notice: the user is
            // standing on the login screen and gets the error directly.
            clearSessionToken();
            store.dispatch(sessionCleared('none'));
            throw error;
        }
        const actor = actorFromEmployee(created.employee);
        store.dispatch(sessionEstablished(actor));
        return actor;
    }
};

/**
 * Signs out: revoke on the Backend, then clear locally -- in that order,
 * and the local clear happens EITHER WAY. A user must always be able to
 * sign out of the till in front of them, even with no network. When the
 * remote revoke fails the token stays valid on the Backend until its
 * expiry, which the caller should say without showing the token.
 */
export const signOut = async (store: AuthStore): Promise<SignOutResult> => {
    let revokedRemotely = false;
    try {
        await session_revoke();
        revokedRemotely = true;
    } catch (e) {
        // Reported to the caller through the result; never blocks sign-out.
    } finally {
        // The user pressed Logout: they know why they are on the login
        // page, so no notice.
        clearSessionToken();
        store.dispatch(sessionCleared('none'));
    }
    return { revokedRemotely };
};

/**
 * Central reaction to a 401 on a request that carried our token.
 *
 * A screen can easily have three requests in flight when a session dies;
 * without this guard each one would clear storage and cause its own
 * navigation. The guard is the store itself: once the status is
 * `signedOut`, every later concurrent 401 is a no-op.
 */
export const handleUnauthenticated = (store: AuthStore): void => {
    if (store.getState().userReducer.authStatus === 'signedOut') return;
    // Mid-session, on a request the user did not expect to fail: this is
    // the "another device took the session" case they actually see.
    clearSessionToken();
    store.dispatch(sessionCleared('sessionEnded'));
};

/**
 * The way out of `unreachable`: drop the local token WITHOUT a remote
 * revoke (the Backend is, by definition, not reachable) and go to login.
 * The Backend expiry remains the final bound on that session.
 */
export const forgetLocalSession = (store: AuthStore): void => {
    // The user chose to go back to login from the "cannot reach the
    // server" screen. Nothing was taken from them, so no notice.
    clearSessionToken();
    store.dispatch(sessionCleared('none'));
};
